import React, { useState } from "react";
import PropTypes from "prop-types";

import {
  getPriorityInfo,
  getStatusBadge,
  priorityTextToNumber,
} from "./taskUtils";

export const DashboardBottom = ({
  tasks,
  usersList,
  modules = [],
  onOpenDetail,
  onViewAll,
}) => {
const recentTasks = [...tasks].sort((a, b) => 
  new Date(b.updatedDate ?? b.dueDate) - new Date(a.updatedDate ?? a.dueDate)
);
  const [chartTab, setChartTab] = useState("priority");

  const priorityCounts = {
    High: {
      count: tasks.filter((t) => priorityTextToNumber(t.priority) === 1).length,
      color: "#EF4444",
    },
    Medium: {
      count: tasks.filter((t) => priorityTextToNumber(t.priority) === 2).length,
      color: "#F59E0B",
    },
    Low: {
      count: tasks.filter((t) => priorityTextToNumber(t.priority) === 3).length,
      color: "#10B981",
    },
  };
  const total = tasks.length || 1;

  return (
<div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
      {/* Recent Tasks — spans 2 cols */}
<div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col h-fit">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <span className="font-bold text-gray-900 flex items-center gap-2">
            <i className="fas fa-clock text-blue-600" /> Recently Updated Tasks
          </span>
          <button
            className="text-xs font-semibold text-blue-600 hover:underline"
            onClick={onViewAll}
          >
            View All →
          </button>
        </div>
<div className="overflow-x-auto" style={{ maxHeight: "362px", overflowY: "auto" }}>
          <table className="w-full">
<thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                {[
                  "ID",
                  "Title",
                  "Status",
                  "Priority",
                  "Assigned To",
                  "Due Date",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {recentTasks.map(
                (
                  task,
                  index, // ← add index to the .map()
                ) => (
                  <tr
                    key={task.id}
                    className="hover:bg-gray-50 cursor-pointer transition"
                    onClick={() => onOpenDetail(task)}
                  >
                    <td className="px-4 py-3 text-xs font-bold text-gray-700 font-mono">
                      {task.displayId ?? index + 1}
                    </td>

                    <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-[200px] truncate">
                      {task.subject}
                    </td>
                    <td className="px-4 py-3">{getStatusBadge(task.status)}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityInfo(task.priority).color}`}
                      >
                        {getPriorityInfo(task.priority).text}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {(() => {
                        const assignedUser = usersList.find(
                          (u) => u.id === task.assignedToId,
                        );
                        return assignedUser
                          ? `${assignedUser.first_name} ${assignedUser.last_name}`
                          : "—";
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {new Date(task.dueDate).toLocaleDateString()}
                    </td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Right column: Priority chart + Status overview */}
<div className="flex flex-col gap-3">
        {/* Priority / Module Chart */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-bold text-gray-900 flex items-center gap-2">
              <i
                className={`fas ${chartTab === "priority" ? "fa-chart-bar" : "fa-cubes"} text-blue-600`}
              />
              {chartTab === "priority"
                ? "Tasks by Priority"
                : "Tasks by Module"}
            </span>
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-lg p-0.5">
              {["priority", "module"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setChartTab(tab)}
                  className={`px-2.5 py-1 rounded-md text-xs font-bold transition-all capitalize ${
                    chartTab === tab
                      ? "bg-white text-blue-600 shadow-sm"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <div className="px-4 py-3 flex flex-col gap-2.5">
            {chartTab === "priority"
              ? Object.entries(priorityCounts).map(
                  ([label, { count, color }]) => (
                    <div
                      key={label}
                      className="grid grid-cols-[70px_1fr_28px] gap-2 items-center"
                    >
                      <span className="text-xs font-bold text-gray-600">
                        {label}
                      </span>
                      <div className="h-2 rounded-full bg-gray-100 overflow-hidden border border-gray-200">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${(count / total) * 100}%`,
                            background: color,
                          }}
                        />
                      </div>
                      <span className="text-xs font-bold text-gray-500 text-right">
                        {count}
                      </span>
                    </div>
                  ),
                )
: (() => {
    const moduleColors = [
      "#3B82F6",
      "#8B5CF6",
      "#10B981",
      "#F59E0B",
      "#EF4444",
      "#06B6D4",
      "#EC4899",
    ];

    // Build rows from modules — count tasks whose moduleId matches
    // Also handle: moduleId may be null/undefined on tasks from /api/Task/all
    // So we show all modules with 0 count hidden, plus a "No Module" row
    const taskModuleIds = new Set(
      tasks
        .filter((t) => t.moduleId)
        .map((t) => String(t.moduleId).toLowerCase())
    );

    const allModuleIds = new Set(
      modules.map((m) => String(m.moduleId).toLowerCase())
    );

    // Check if ANY task has a moduleId at all
    const hasModuleData = taskModuleIds.size > 0;

    const rows = hasModuleData
      ? modules
          .map((m) => ({
            name: m.moduleName,
            count: tasks.filter(
              (t) =>
                t.moduleId &&
                String(t.moduleId).toLowerCase() ===
                  String(m.moduleId).toLowerCase()
            ).length,
          }))
          .filter((r) => r.count > 0)
          .sort((a, b) => b.count - a.count)
          .slice(0, 5)
      : [];

    const unassigned = tasks.filter(
      (t) =>
        !t.moduleId ||
        !allModuleIds.has(String(t.moduleId).toLowerCase())
    ).length;

    if (unassigned > 0) {
      rows.push({ name: "No Module", count: unassigned });
    }

    // If tasks have no moduleId field at all (backend doesn't return it),
    // show modules list with task count as 0 and a helpful message
    if (!hasModuleData && modules.length > 0) {
      return (
        <div className="py-3 flex flex-col gap-2">
          <div className="flex items-center gap-2 px-1 mb-1">
            <div className="flex-1 h-px bg-amber-100" />
            <span className="text-[10px] font-bold text-amber-500 uppercase tracking-wider whitespace-nowrap">
              Module data not in task list
            </span>
            <div className="flex-1 h-px bg-amber-100" />
          </div>
          {modules.slice(0, 5).map((m, i) => (
            <div
              key={m.moduleId}
              className="grid grid-cols-[90px_1fr_28px] gap-2 items-center"
            >
              <span
                className="text-xs font-bold text-gray-500 truncate"
                title={m.moduleName}
              >
                {m.moduleName.length > 13
                  ? `${m.moduleName.slice(0, 12)}…`
                  : m.moduleName}
              </span>
              <div className="h-2 rounded-full bg-gray-100 overflow-hidden border border-gray-200">
                <div
                  className="h-full rounded-full opacity-30"
                  style={{
                    width: "100%",
                    background: moduleColors[i % moduleColors.length],
                  }}
                />
              </div>
              <span className="text-xs font-bold text-gray-300 text-right">
                —
              </span>
            </div>
          ))}
          <p className="text-[10px] text-amber-500 text-center mt-1 font-medium">
            {/* Ask backend to include moduleId in task list API */}
          </p>
        </div>
      );
    }

    if (rows.length === 0) {
      return (
        <div className="py-4 text-center">
          <i className="fas fa-cubes text-gray-200 text-2xl mb-2 block" />
          <p className="text-xs font-bold text-gray-400">
            No tasks assigned to modules
          </p>
          <p className="text-xs text-gray-300 mt-0.5">
            {modules.length > 0 &&
              `${modules.length} module${modules.length > 1 ? "s" : ""} available`}
          </p>
        </div>
      );
    }

    const maxVal = Math.max(...rows.map((r) => r.count), 1);

    return rows.map(({ name, count }, i) => (
      <div
        key={name}
        className="grid grid-cols-[90px_1fr_28px] gap-2 items-center"
      >
        <span
          className="text-xs font-bold text-gray-600 truncate"
          title={name}
        >
          {name.length > 13 ? `${name.slice(0, 12)}…` : name}
        </span>
        <div className="h-2 rounded-full bg-gray-100 overflow-hidden border border-gray-200">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${(count / maxVal) * 100}%`,
              background:
                name === "No Module"
                  ? "#D1D5DB"
                  : moduleColors[i % moduleColors.length],
            }}
          />
        </div>
        <span className="text-xs font-bold text-gray-500 text-right">
          {count}
        </span>
      </div>
    ));
  })()}
          </div>
        </div>

        {/* Status Overview */}
        {/* Team Workload */}
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <span className="font-bold text-gray-900 flex items-center gap-2">
              <i className="fas fa-users text-blue-600" /> Team Workload
            </span>
            <span className="text-xs font-bold text-gray-400">
              {tasks.length} tasks
            </span>
          </div>
<div className="px-4 py-3 flex flex-col gap-2" style={{ maxHeight: "200px", overflowY: "auto" }}>
            {usersList.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-3">
                No team members found
              </p>
            ) : (
              (() => {
                const workload = usersList
                  .map((u) => ({
                    name: `${u.first_name} ${u.last_name}`,
                    initials:
                      `${u.first_name?.[0] ?? ""}${u.last_name?.[0] ?? ""}`.toUpperCase(),
                    count: tasks.filter((t) => t.assignedToId === u.id).length,
                  }))
.filter((w) => w.count > 0)
.sort((a, b) => b.count - a.count);

                if (workload.length === 0)
                  return (
                    <p className="text-xs text-gray-400 text-center py-3">
                      No assigned tasks
                    </p>
                  );

                const max = workload[0].count;
                const avatarColors = [
                  { bg: "#EFF6FF", text: "#2563EB" },
                  { bg: "#F5F3FF", text: "#7C3AED" },
                  { bg: "#FFF7ED", text: "#C2410C" },
                  { bg: "#ECFDF5", text: "#059669" },
                  { bg: "#FEF2F2", text: "#DC2626" },
                ];

                return workload.map(({ name, initials, count }, i) => {
                  const { bg, text } = avatarColors[i % avatarColors.length];
                  return (
                    <div key={name} className="flex items-center gap-2">
                      <div
                        className="w-6 h-6 rounded-md flex items-center justify-center text-[10px] font-bold flex-shrink-0"
                        style={{ background: bg, color: text }}
                      >
                        {initials}
                      </div>
                      <span className="text-xs font-semibold text-gray-700 truncate w-20 flex-shrink-0">
                        {name.split(" ")[0]}
                      </span>
                      <div className="flex-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${(count / max) * 100}%`,
                            background: text,
                          }}
                        />
                      </div>
                      <span className="text-xs font-bold text-gray-500 w-4 text-right flex-shrink-0">
                        {count}
                      </span>
                    </div>
                  );
                });
              })()
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

DashboardBottom.propTypes = {
  modules: PropTypes.arrayOf(
    PropTypes.shape({
      moduleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      moduleName: PropTypes.string,
    }),
  ),

  tasks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      subject: PropTypes.string,
      status: PropTypes.string,
      priority: PropTypes.string,
      assignedToId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      dueDate: PropTypes.string,
    }),
  ).isRequired,
  usersList: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      first_name: PropTypes.string,
      last_name: PropTypes.string,
    }),
  ).isRequired,
  onOpenDetail: PropTypes.func.isRequired,
  onViewAll: PropTypes.func.isRequired,
};
